import {Lifetime, LifetimeSequence, listenDocumentEvent} from "./Lifetime";
import {Logger} from "./Logger";
import {AsyncChannel} from "./concurrency/AsyncChannel";
import {delay, delayed} from "./concurrency/Delay";

/**
 * HID Omnikey reader handler that detects NFC card IDs typed as 14-character strings
 */

const MAX_INPUT_DELAY_MS = 200; // Maximum time between characters for a valid NFC read

type NfcIdFormat = {
    name: string,
    regexp: RegExp,
}

const NFC_ID_FORMATS: ReadonlyArray<NfcIdFormat> = [
    {
        name: 'ISO7816 (Type4): Berlin, Munich',
        regexp: /^(04[0-9A-F]{12})$/
    },
    {
        name: 'MIFARE UID: Prague',
        regexp: /^(0000[0-9A-F]{12})$/
    },
];

// Maps physical key codes to hex characters, so keyboard layout changes don't affect NFC reading.
// Keyboard wedge readers send physical key positions (scancodes); the browser then applies the
// active layout to produce event.key. Using event.code bypasses this layout mapping.
const CODE_TO_HEX: Readonly<Record<string, string>> = {
    Digit0: '0', Digit1: '1', Digit2: '2', Digit3: '3', Digit4: '4',
    Digit5: '5', Digit6: '6', Digit7: '7', Digit8: '8', Digit9: '9',
    KeyA: 'A', KeyB: 'B', KeyC: 'C', KeyD: 'D', KeyE: 'E', KeyF: 'F',
};

const ENTER_CODE = 'Enter';

function codeToHex(code: string): string | undefined {
    return CODE_TO_HEX[code];
}

/**
 * Listens for NFC reader signals through keyboard events and processes them to detect NFC card IDs.
 *
 * @param {Lifetime} lifetime - Controls the lifecycle of the NFC listening process. When terminated, it stops listening for events.
 * @param {(nfcId: string) => void} onNfcRead - Callback invoked when an NFC ID is successfully read and parsed.
 * @param {(progress: number) => void} onReadingProgress - Callback invoked to provide real-time feedback on the reading progress, where the progress value [0, 1] reflects the current status.
 * @return {void} No return value.
 */
export function listenNfcReader(
    lifetime: Lifetime,
    onNfcRead: (nfcId: string) => void,
    onReadingProgress: (progress: number) => void,
): void {
    const log = Logger('listenNfcReader', {withId: true});

    const keyboardEventChannel = new AsyncChannel<string>(lifetime.nested());

    async function matchNfcCardJob() {
        const keyboardEvents = keyboardEventChannel.incoming();

        let sequence = '';
        for await (const keyCode of keyboardEvents) {
            if (keyCode !== ENTER_CODE) {
                const hex = codeToHex(keyCode);
                if (hex === undefined) {
                    sequence = '';
                    onReadingProgress(0);
                    log.debug(`Non-hex key code ${keyCode}. Start over.`);
                } else {
                    sequence += hex;
                    const progress = Math.min(sequence.length / 14, 1.0);
                    onReadingProgress(progress);
                    log.debug(`Append character: [${sequence}], progress: ${progress}`)
                }
            } else {
                log.debug(`End of sequence [${sequence}]`);
                if (sequence.length > 0) {
                    for (const format of NFC_ID_FORMATS) {
                        const match = format.regexp.exec(sequence);
                        if (match) {
                            const nfcId = match[1];
                            log(`Detected NFC ID: ${nfcId}, ${format.name}`);
                            onNfcRead(nfcId)
                            break;
                        }
                    }
                    onReadingProgress(0);
                    sequence = '';
                }
            }
        }
    }

    const pauseLifetimeSequence = new LifetimeSequence(lifetime);
    const handleKeyPress = (event: KeyboardEvent): void => {
        log.debug(`Key pressed: code='${event.code}' key='${event.key}' hex='${codeToHex(event.code)}'`);
        const pauseLifetime = pauseLifetimeSequence.next();
        keyboardEventChannel.send(event.code);
        if (event.code !== ENTER_CODE) {
            delayed(MAX_INPUT_DELAY_MS, pauseLifetime, () => {
                keyboardEventChannel.send(ENTER_CODE);
            });
        }
    };

    matchNfcCardJob();

    log('Start listening');
    listenDocumentEvent(lifetime, 'keypress', handleKeyPress, true)
    lifetime.onTermination(() => log('Stop listening'));
}

export const NFC_KEY_DETECTED_EVENT = 'nfc-key-detected';
export const NFC_KEY_READING_EVENT = 'nfc-key-reading';

async function emitNfcKeyDetected(transponderId: string) {
    // Wait for all keyboard events to be processed
    await delay(50);
    window.dispatchEvent(new CustomEvent(NFC_KEY_DETECTED_EVENT, {detail: transponderId}));
}

function emitNfcKeyReading(progress: number) {
    window.dispatchEvent(new CustomEvent(NFC_KEY_READING_EVENT, {detail: progress}));
}

export function translateNfcKeyDetectedEvents(lifetime: Lifetime): void {
    listenNfcReader(
        lifetime,
        nfcId => emitNfcKeyDetected(nfcId),
        progress => emitNfcKeyReading(progress),
    );
}
